import { Box, TableCell, TableRow, Typography } from '@mui/material';
import { ComponentType, MouseEvent, ReactNode, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { CategoryLabel } from 'entities/Category';
import { Proposal } from 'entities/Proposal/model/types/ProposalSchema';
import { getPrice } from 'shared/lib/getPrice/getPrice';
import { getTermFromCode } from 'shared/lib/unitOfMeasureUtility/unitOfMeasureUtility';
import { CatalogItemImage } from 'shared/ui/CatalogItemImage';
import { useImageLightbox } from 'shared/ui/ImageLightbox';
import { SupplierBadgeWithTooltip } from 'shared/ui/Suppliers/ui/SupplierBadgeWithTooltip';

import {
    CATALOG_PRODUCT_THUMB_PX,
    catalogProductCategoryTextSx,
    catalogProductColumnWidths,
    catalogProductDataCellSx,
    catalogProductItemTitleSx,
    catalogProductPriceTypographySx,
    catalogProductRowChromeSx,
    catalogProductUomCellTypographySx,
} from './catalogProductTableSpec';

import type { SxProps, Theme } from '@mui/material/styles';

export interface CatalogProductTableRowProduct {
    title?: string;
    thumbnailUrl?: string;
    imageUrl?: string;
    categoryId?: string;
    uom?: string;
    proposals?: Proposal[];
    price?: number;
}

interface CatalogProductTableRowProps {
    product: CatalogProductTableRowProduct;
    leading?: ReactNode;
    quantityCell: ReactNode;
    showPrice?: boolean;
    /** When true, renders a 40px status column before trailing (e.g. availability). */
    showStatusColumn?: boolean;
    statusCell?: ReactNode;
    trailing?: ReactNode;
    onClick?: () => void;
    dataTestClass?: string;
    titleDataTestClass?: string;
    /**
     * Table row wrapper. Use inventory `DraggableTableRow` when reorder is needed.
     * Must render an MUI `TableRow` (or compatible) and accept `children` + `sx`.
     * Typed loosely so domain-specific row wrappers (required DnD props, etc.) type-check.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component?: ComponentType<any>;
    rowProps?: Record<string, unknown>;
}

export const CatalogProductTableRow = memo(
    ({
        product,
        leading,
        quantityCell,
        showPrice = false,
        showStatusColumn = false,
        statusCell,
        trailing,
        onClick,
        dataTestClass = 'catalog-product-table-row',
        titleDataTestClass = 'catalog-product-item-title',
        component,
        rowProps,
    }: CatalogProductTableRowProps) => {
        const { t } = useTranslation();
        const { openLightbox } = useImageLightbox();
        const RowComponent = component ?? TableRow;
        const thumbSrc = product.thumbnailUrl || product.imageUrl;

        const onThumbClick = useCallback(
            (event: MouseEvent) => {
                event.stopPropagation();
                if (thumbSrc) {
                    openLightbox(thumbSrc);
                }
            },
            [openLightbox, thumbSrc],
        );

        const rowSx = [
            catalogProductRowChromeSx,
            rowProps?.sx as SxProps<Theme> | undefined,
        ].filter(Boolean) as SxProps<Theme>;

        return (
            <RowComponent
                {...rowProps}
                onClick={onClick}
                data-testclass={dataTestClass}
                sx={rowSx}
            >
                <TableCell
                    sx={{
                        ...catalogProductDataCellSx,
                        padding: '15px 16px',
                        width: catalogProductColumnWidths.item,
                        maxWidth: catalogProductColumnWidths.item,
                        boxSizing: 'border-box',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            minWidth: 0,
                        }}
                    >
                        {leading ? (
                            <Box
                                sx={{
                                    width: '24px',
                                    height: '24px',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                onClick={(event) => event.stopPropagation()}
                            >
                                {leading}
                            </Box>
                        ) : null}
                        <Box
                            onClick={onThumbClick}
                            sx={{
                                width: `${CATALOG_PRODUCT_THUMB_PX}px`,
                                height: `${CATALOG_PRODUCT_THUMB_PX}px`,
                                minWidth: `${CATALOG_PRODUCT_THUMB_PX}px`,
                                minHeight: `${CATALOG_PRODUCT_THUMB_PX}px`,
                                maxWidth: `${CATALOG_PRODUCT_THUMB_PX}px`,
                                maxHeight: `${CATALOG_PRODUCT_THUMB_PX}px`,
                                borderRadius: '4px',
                                cursor: thumbSrc ? 'pointer' : 'default',
                                flexShrink: 0,
                                overflow: 'hidden',
                                '&:hover': {
                                    div: {
                                        borderColor: '#bababa',
                                    },
                                },
                            }}
                        >
                            <CatalogItemImage
                                src={thumbSrc}
                                height={`${CATALOG_PRODUCT_THUMB_PX}px`}
                                width={`${CATALOG_PRODUCT_THUMB_PX}px`}
                                showBorder={false}
                            />
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                                alignItems: 'flex-start',
                                minWidth: 0,
                                flex: 1,
                            }}
                        >
                            <Typography
                                data-testclass={titleDataTestClass}
                                sx={catalogProductItemTitleSx}
                            >
                                {product.title}
                            </Typography>
                            {product.proposals?.length ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 400,
                                            fontSize: '1.1rem',
                                            lineHeight: 'normal',
                                            color: 'rgba(0, 0, 0, 0.5)',
                                        }}
                                    >
                                        {t('Carried by')}
                                    </Typography>
                                    <SupplierBadgeWithTooltip
                                        fullProposals={product.proposals}
                                    />
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                </TableCell>
                <TableCell
                    data-testclass="catalog-product-item-category"
                    align="left"
                    sx={{
                        ...catalogProductDataCellSx,
                        width: catalogProductColumnWidths.category,
                        boxSizing: 'border-box',
                    }}
                >
                    {product.categoryId ? (
                        <CategoryLabel
                            categoryId={product.categoryId}
                            textSx={catalogProductCategoryTextSx}
                        />
                    ) : null}
                </TableCell>
                <TableCell
                    data-testclass="catalog-product-item-uom"
                    sx={{
                        ...catalogProductDataCellSx,
                        boxSizing: 'border-box',
                        width: catalogProductColumnWidths.uom,
                    }}
                    align="center"
                >
                    <Typography
                        align="center"
                        sx={catalogProductUomCellTypographySx}
                    >
                        {getTermFromCode(product.uom, true) || product.uom}
                    </Typography>
                </TableCell>
                <TableCell
                    data-testclass="catalog-product-item-quantity"
                    sx={{
                        ...catalogProductDataCellSx,
                        boxSizing: 'border-box',
                        width: catalogProductColumnWidths.minOnHand,
                    }}
                    align="center"
                    onClick={(event) => event.stopPropagation()}
                >
                    {quantityCell}
                </TableCell>
                {showPrice ? (
                    <TableCell
                        data-testclass="catalog-product-item-price"
                        sx={{
                            ...catalogProductDataCellSx,
                            width: '84px',
                            maxWidth: '84px',
                            boxSizing: 'content-box',
                        }}
                        align="right"
                    >
                        <Typography sx={catalogProductPriceTypographySx}>
                            {getPrice(product.price)}
                        </Typography>
                    </TableCell>
                ) : null}
                {showStatusColumn ? (
                    <TableCell
                        data-testclass="catalog-product-item-status"
                        sx={{
                            ...catalogProductDataCellSx,
                            width: '40px',
                        }}
                        align="center"
                        onClick={(event) => event.stopPropagation()}
                    >
                        {statusCell}
                    </TableCell>
                ) : null}
                {trailing ? (
                    <TableCell
                        data-testclass="catalog-product-item-actions"
                        sx={{
                            ...catalogProductDataCellSx,
                            width: catalogProductColumnWidths.buttons,
                            boxSizing: 'content-box',
                            textAlign: 'right',
                            paddingRight: '12px',
                        }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        {trailing}
                    </TableCell>
                ) : null}
            </RowComponent>
        );
    },
);

CatalogProductTableRow.displayName = 'CatalogProductTableRow';
